import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Citrus, FlaskConical } from "lucide-react";
import { api, rupees } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { FadeUp, MaskedLines, RevealImage } from "@/components/Reveal";

const FALLBACK = [
  {
    sku: "vibe-30",
    name: "The Morning Pack",
    tagline: "One sachet every morning, with or after breakfast.",
    sachets: 15,
    price_paise: 79900,
    image:
      "https://static.prod-images.emergentagent.com/jobs/ea37c7fa-2a53-4871-a4e9-78d1a2debf12/images/fb3146d8cbd24bd4f83c278e3fa3161c35d21ce9edcece88cce97b50ee193bec.jpeg",
    badge: "15-day pack",
  },
];

const USPS = {
  heading: "What Vibe's Got",
  items: [
    {
      lead: "Zinc + Niacinamide (Vitamin B3):",
      text: "Helps balance oil production and calm the inflammation behind breakouts.",
    },
    {
      lead: "Vitamin D3:",
      text: "Supports skin's natural repair and immune balance for a calmer complexion.",
    },
    {
      lead: "Probiotics (L. rhamnosus, L. plantarum) + Vitamin C:",
      text: "Support gut health, which is linked to clearer skin, while Vitamin C adds antioxidant protection and a natural glow.",
    },
  ],
};

const EXTRAS = [
  {
    icon: Citrus,
    main: "Easy, tasty, sugar-free",
    sub: "A once-a-day powder you mix in water — refreshing citrus flavour, sweetened with stevia, no added sugar, no harsh chemicals.",
  },
];

export default function Shop() {
  const { addItem } = useCart();
  const [products, setProducts] = useState(FALLBACK);

  useEffect(() => {
    api
      .get("/api/products")
      .then((res) => res.data?.length && setProducts(res.data))
      .catch(() => {});
  }, []);

  const product = products[0];

  return (
    <div data-testid="page-shop">
      <section className="mx-auto max-w-[88rem] px-5 sm:px-10 pt-[8.5rem] lg:pt-[10.5rem] pb-24 lg:pb-36">
        <div className="mb-14 lg:mb-20">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.26em] text-[#4A6B56] mb-6">The shop</p>
          </FadeUp>
          <h1 className="font-display text-[clamp(2.7rem,5.5vw,5rem)] leading-[1.03] text-[#1B4332]">
            <MaskedLines lines={["Pick your", "morning ritual."]} />
          </h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid="shop-product-grid">
          <div className="lg:col-span-7">
            <RevealImage>
              <div className="relative">
                <img
                  src={product.image}
                  alt={`${product.name} — Vibe acne-care sachets`}
                  className="w-full aspect-[4/3] object-cover rounded-2xl"
                  data-testid={`product-image-${product.sku}`}
                />
                {product.badge && (
                  <span className="absolute top-5 left-5 rounded-full bg-[#1B4332] text-[#E8F3EB] text-xs px-4 py-2 tracking-wide">
                    {product.badge}
                  </span>
                )}
              </div>
            </RevealImage>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.1}>
              <h2 className="font-display text-4xl sm:text-5xl text-[#1B4332]" data-testid={`product-name-${product.sku}`}>
                {product.name}
              </h2>
              <p className="mt-3 text-lg text-[#4A6B56]">{product.tagline}</p>

              <div className="mt-8 flex items-baseline gap-4">
                <p className="font-display text-5xl text-[#1B4332]" data-testid={`product-price-${product.sku}`}>
                  {rupees(product.price_paise)}
                </p>
                <p className="text-sm text-[#4A6B56]">
                  {product.sachets} sachets
                </p>
              </div>

              <button
                data-testid={`add-to-cart-${product.sku}`}
                onClick={() => addItem(product)}
                className="mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-[#1B4332] text-[#E8F3EB] pl-10 pr-8 h-16 text-lg font-medium hover:bg-[#2D5A44] active:scale-[0.98] transition-all duration-300"
              >
                Add to cart
                <ArrowUpRight size={19} />
              </button>
              <p className="mt-4 text-sm text-[#4A6B56]">Free shipping across India.</p>

              <div className="mt-10 space-y-6 border-t border-[#D1E2D6] pt-8" data-testid="shop-usps">
                <div className="flex items-start gap-4" data-testid="usp-what-vibes-got">
                  <span className="grid place-items-center h-11 w-11 rounded-full border border-[#C6DCCB] text-[#1B4332] shrink-0 mt-0.5">
                    <FlaskConical size={17} strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="font-semibold text-[#1B4332] leading-snug">{USPS.heading}</p>
                    <div className="mt-2 space-y-2.5">
                      {USPS.items.map((item, j) => (
                        <p key={j} className="text-sm text-[#4A6B56] leading-relaxed">
                          <span className="font-semibold text-[#1B4332]">{item.lead}</span>{" "}
                          {item.text}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                {EXTRAS.map((extra, i) => (
                  <div key={i} className="flex items-start gap-4" data-testid={`usp-${i + 1}`}>
                    <span className="grid place-items-center h-11 w-11 rounded-full border border-[#C6DCCB] text-[#1B4332] shrink-0 mt-0.5">
                      <extra.icon size={17} strokeWidth={1.6} />
                    </span>
                    <div>
                      <p className="font-semibold text-[#1B4332] leading-snug">{extra.main}</p>
                      <p className="mt-1 text-sm text-[#4A6B56] leading-relaxed">{extra.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-8 text-xs text-[#4A6B56] leading-relaxed">
                Product information is not medical advice. Results are not guaranteed.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
